// =====================================================================
// src/pages/intl-acca/SlipArchivePage.tsx
// =====================================================================
// Historical slips with leg-level CLV resolution (PRD §3 — "Slip
// Archive · proof of process"). Tier-gated: Sika Pro+.
//
// Routes mounted at: /intl-acca/archive
//
// EMPTY PATH: archive is "wired but empty" until any slip lands in
// status WON / LOST / VOID. This page renders that empty state
// honestly — never seeds with mock rows.

import { useMemo, useState } from "react";
import type { ResultStatus } from "../../db/intl-acca-types";
import { formatClvPp, formatOdds } from "../../db/intl-acca-types";
import type { SlipArchiveRow } from "../../db/intl-acca-types-extended";
import { HonestEmptyState } from "../../components/intl-acca/HonestEmptyState";
import { TierLockedTeaser } from "../../components/intl-acca/TierLockedTeaser";

export interface SlipArchivePageProps {
  loading: boolean;
  error: string | null;
  rows: SlipArchiveRow[];
  /** Sika Pro+ gate (PRD §3.2). Host computes tierMeets(tier,'sika_pro'). */
  hasAccess: boolean;
  /** Click a row to expand its leg-level CLV table inline. */
  onOpenSlip?: (slipId: string) => void;
  /** Wired to the pricing route when the archive is tier-locked. */
  onUpgrade?: () => void;
}

type FilterId = "all" | "focus" | "value" | "power" | "won" | "lost-clv+";

const FILTERS: Array<{ id: FilterId; label: string }> = [
  { id: "all",       label: "All" },
  { id: "focus",     label: "Focus · 3-leg" },
  { id: "value",     label: "Value · 5-leg" },
  { id: "power",     label: "Power · 10-leg" },
  { id: "won",       label: "Wins" },
  { id: "lost-clv+", label: "Losses · CLV+" },
];

function applyFilter(rows: SlipArchiveRow[], f: FilterId): SlipArchiveRow[] {
  switch (f) {
    case "all":       return rows;
    case "focus":     return rows.filter((r) => r.slip_type === "FOCUS");
    case "value":     return rows.filter((r) => r.slip_type === "VALUE");
    case "power":     return rows.filter((r) => r.slip_type === "POWER");
    case "won":       return rows.filter((r) => r.status === "WON");
    case "lost-clv+": return rows.filter((r) => r.status === "LOST" && (r.clv_score_final ?? 0) > 0);
  }
}

export function SlipArchivePage(props: SlipArchivePageProps): JSX.Element {
  const { loading, error, rows, hasAccess, onOpenSlip, onUpgrade } = props;
  const [filter, setFilter] = useState<FilterId>("all");

  const summary = useMemo(() => {
    const total = rows.length;
    const won = rows.filter((r) => r.status === "WON").length;
    const settled = rows.filter((r) => r.clv_score_final != null);
    const clvPos = settled.filter((r) => (r.clv_score_final ?? 0) > 0).length;
    const avgClv = settled.length
      ? settled.reduce((s, r) => s + (r.clv_score_final ?? 0), 0) / settled.length
      : null;
    return { total, won, clvPos, settled: settled.length, avgClv };
  }, [rows]);

  if (loading) {
    return (
      <div className="ia-root ia-page-pad" aria-busy="true">
        <div className="ia-eyebrow">Loading archive…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState variant="custom" title="Archive unavailable." body={error} />
      </div>
    );
  }

  const filtered = applyFilter(rows, filter);

  const content = (
    <div className="ia-root">
      <header className="ia-page-header">
        <div>
          <div className="ia-eyebrow">Process audit · proof of process</div>
          <h1>
            Slip Archive.{" "}
            <em>Every slip, every leg, the closing line.</em>
          </h1>
          <p className="ia-sub">
            Every slip STRIKZA has issued — won, lost, voided — with the
            CLV resolution on every leg. Click any row to inspect its
            leg-level audit.
          </p>
        </div>
      </header>

      {/* Stat strip */}
      <div
        style={{
          padding: "var(--sz-space-5) var(--sz-space-7)",
          borderBottom: "1px solid var(--border-faint)",
          background: "var(--bg-elev)",
          display: "flex",
          gap: 0,
        }}
      >
        <SummaryCell label="WIN RATE" value={summary.total ? `${Math.round((summary.won / summary.total) * 100)}%` : "—"} sub={`${summary.won} of ${summary.total}`} />
        <SummaryCell label="CLV+ RATE" value={summary.settled ? `${Math.round((summary.clvPos / summary.settled) * 100)}%` : "—"} sub={`${summary.clvPos} of ${summary.settled} settled`} accent />
        <SummaryCell label="AVG CLV" value={summary.avgClv == null ? "—" : formatClvPp(summary.avgClv)} sub="across settled slips" accent />
      </div>

      {/* Filters */}
      <div
        style={{
          padding: "var(--sz-space-4) var(--sz-space-7)",
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          borderBottom: "1px solid var(--border-faint)",
        }}
      >
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`ia-pill ${filter === f.id ? "ia-pill-outline" : "ia-pill-ghost"}`}
            style={{ cursor: "pointer", padding: "8px 14px", fontSize: 10.5 }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="ia-page-pad">
        {filtered.length === 0 ? (
          <HonestEmptyState
            variant="custom"
            title={rows.length === 0 ? "No slips settled yet." : "No slips match this filter."}
            body={
              rows.length === 0
                ? "The archive populates as slips move from ACTIVE to WON / LOST / VOID. Empty by design today."
                : "Try a wider filter, or wait for more slips to settle."
            }
          />
        ) : (
          filtered.map((r) => (
            <ArchiveRow key={r.slip_id} row={r} onOpen={() => onOpenSlip?.(r.slip_id)} />
          ))
        )}
      </div>
    </div>
  );

  if (!hasAccess) {
    return (
      <div className="ia-root">
        <div className="ia-page-pad">
          <TierLockedTeaser gate="slip_archive" onUpgrade={onUpgrade}>
            {content}
          </TierLockedTeaser>
        </div>
      </div>
    );
  }

  return content;
}

function SummaryCell({
  label, value, sub, accent,
}: {
  label: string; value: string; sub: string; accent?: boolean;
}): JSX.Element {
  return (
    <div style={{ padding: "12px 32px", borderRight: "1px solid var(--border-faint)" }}>
      <div className="ia-metric-label">{label}</div>
      <div
        style={{
          fontFamily: "var(--sz-font-display)",
          fontSize: 28,
          lineHeight: 1,
          marginTop: 6,
          color: accent ? "var(--accent-text)" : "var(--text)",
        }}
      >
        {value}
      </div>
      <div className="ia-metric-sub">{sub}</div>
    </div>
  );
}

function ArchiveRow({
  row, onOpen,
}: {
  row: SlipArchiveRow;
  onOpen: () => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const won = row.status === "WON";
  const clvPositive = (row.clv_score_final ?? 0) > 0;
  const variantClass = won
    ? "won"
    : row.status === "LOST" && clvPositive
      ? "lost-clv-pos"
      : "lost";

  return (
    <div className={`ia-archive-row ${variantClass}`}>
      <div
        className="ia-archive-row-head"
        onClick={() => setExpanded((e) => !e)}
        role="button"
        aria-expanded={expanded}
      >
        <span
          className="ia-mono"
          style={{
            fontSize: "var(--sz-mono-sm)",
            color: "var(--text-tertiary)",
            letterSpacing: "0.14em",
          }}
        >
          {row.slip_id.slice(0, 8)}
        </span>
        <span
          className="ia-mono"
          style={{
            fontSize: "var(--sz-mono-sm)",
            color: "var(--text-secondary)",
            letterSpacing: "0.16em",
          }}
        >
          {new Date(row.generated_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
        </span>
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 4,
            }}
          >
            <span
              className={`ia-pill ${
                row.slip_type === "FOCUS" ? "ia-pill-amber" :
                row.slip_type === "VALUE" ? "ia-pill-info"  :
                "ia-pill-warn"
              }`}
            >
              {row.slip_type} · {row.legs_count}L
            </span>
            <span className="ia-pill ia-pill-ghost">
              {row.tournament_name}
            </span>
          </div>
          <div
            style={{
              fontSize: "var(--sz-body-sm)",
              color: "var(--text-secondary)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.legs_highlight.map((l) => l.selection).join(" · ")}
            {row.legs_count > row.legs_highlight.length
              ? ` · +${row.legs_count - row.legs_highlight.length} more`
              : ""}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="ia-metric-label">ODDS</div>
          <div className="ia-mono" style={{ fontSize: 14, color: "var(--text)", marginTop: 4 }}>
            {formatOdds(row.total_odds)}×
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="ia-metric-label">CLV AVG</div>
          <div
            className="ia-mono"
            style={{
              fontSize: 14,
              color: row.clv_score_final == null ? "var(--text-tertiary)" : "var(--accent-text)",
              marginTop: 4,
            }}
          >
            {formatClvPp(row.clv_score_final)}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <span
            className={
              won
                ? "ia-pill ia-pill-pos"
                : clvPositive
                  ? "ia-pill ia-pill-amber"
                  : "ia-pill ia-pill-neg"
            }
          >
            {won ? "WON" : clvPositive ? "LOST · CLV+" : "LOST"}
          </span>
          <div
            className="ia-mono"
            style={{
              fontSize: "var(--sz-mono-sm)",
              color: won ? "var(--pos)" : "var(--text-tertiary)",
              marginTop: 6,
            }}
          >
            {row.payout_per_100 == null ? "—" : `GH₵${row.payout_per_100}`}
          </div>
        </div>
        <span
          className="ia-mono"
          style={{ fontSize: 14, color: "var(--text-tertiary)", textAlign: "center" }}
        >
          {expanded ? "▾" : "▸"}
        </span>
      </div>

      {expanded ? (
        <div className="ia-archive-row-detail">
          <div className="ia-eyebrow">Leg-by-leg CLV resolution</div>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
            {row.legs_highlight.map((l, i) => (
              <LegMini key={i} sel={l.selection} clv={l.clv_pp} result={l.result} />
            ))}
            {row.legs_count > row.legs_highlight.length ? (
              <button
                type="button"
                onClick={onOpen}
                style={{
                  marginTop: 4,
                  alignSelf: "flex-start",
                  background: "transparent",
                  border: "none",
                  color: "var(--accent)",
                  fontFamily: "var(--sz-font-mono)",
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                See full audit · {row.legs_count} legs →
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function LegMini({
  sel, clv, result,
}: {
  sel: string;
  clv: number | null;
  result: ResultStatus | null;
}): JSX.Element {
  const won = result === "WON";
  const lost = result === "LOST";
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "24px 1fr 60px",
        alignItems: "center",
        gap: 14,
        padding: "8px 12px",
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
        borderLeft: `3px solid ${won ? "var(--pos)" : lost ? "var(--neg)" : "var(--text-tertiary)"}`,
      }}
    >
      <span
        className="ia-mono"
        style={{
          fontSize: 12,
          color: won ? "var(--pos)" : lost ? "var(--neg)" : "var(--text-tertiary)",
        }}
      >
        {won ? "✓" : lost ? "✕" : "—"}
      </span>
      <span style={{ fontSize: "var(--sz-body-sm)", color: "var(--text)" }}>{sel}</span>
      <span
        className="ia-mono"
        style={{
          fontSize: "var(--sz-mono-sm)",
          color: clv == null ? "var(--text-tertiary)" : "var(--accent-text)",
          textAlign: "right",
        }}
      >
        {formatClvPp(clv)}
      </span>
    </div>
  );
}

// =====================================================================
// DATA BINDING CONTRACT
// =====================================================================
//
// const { data: rows } = await sb.from('intl_acca_slips_archive_view')   // future view
//                              .select('*')
//                              .neq('status', 'ACTIVE')
//                              .order('generated_at', { ascending: false });
//
// Until the view ships, host computes SlipArchiveRow[] from
// intl_acca_slips + intl_acca_slip_legs + intl_acca_tournaments joins,
// truncating legs_highlight to the top 3 per slip (by clv_pp DESC).
